import {
  toast,
  AlertDialog,
  Button,
  ButtonGroup,
  Checkbox,
  cn,
  Dropdown,
  Label,
} from '@heroui/react';
import { Segment } from '@heroui-pro/react';
/**
 * 大屏页：与知识库作品页相同的顶栏结构；工具条「全部」；大屏瀑布流 + 滚动加载下一页
 */
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type HTMLAttributes,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { CanvasThumbnailPreview } from '@/components/canvas/designer/thumbnails/CanvasThumbnailPreview';
import { parseCanvasData } from '@/components/canvas/document/validation';
import { formatDateTime, formatRelativeTime } from '@hs/shared/utils/formatTime';
import { AnimatePresence, motion } from 'motion/react';
import {
  IconDotsFilled,
  IconEdit,
  IconPhotoCheck,
  IconPhotoPlus,
  IconPhotoX,
  IconPlayerPlay,
  IconShare,
  IconShareOff,
  IconTrash,
} from '@tabler/icons-react';
import {
  buildInitialCanvasContent,
  DEFAULT_SLIDE_SIZE,
} from '@/components/canvas/document/buildInitialCanvasContent';
import { canvasDataFromParsed } from '@/components/canvas/document/serializeDocument';
import { trpc, trpcProxyClient } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import type { Page as PageType } from '@/components/canvas/document/publicTypes';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const DISPLAY_PAGE_SIZE = 12;
/** 无画布数据时的占位竖版比例（宽:高 = 9:16 → padding-bottom = 16/9 × 100%） */
const FALLBACK_PREVIEW_RATIO_PCT = (16 / 9) * 100;

function ThumbnailFill({
  page,
  fit = 'contain',
}: {
  page: PageType;
  fit?: 'contain' | 'cover';
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setSize({ width: w, height: h });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="absolute inset-0 size-full bg-muted">
      {size.width > 0 && size.height > 0 && (
        <CanvasThumbnailPreview
          page={page}
          width={size.width}
          height={size.height}
          fit={fit}
          className="size-full"
        />
      )}
    </div>
  );
}

/**
 * 瀑布流预览区：列宽撑满 Masonry 列，高度按画布逻辑尺寸比例变化（不固定 9:16）。
 * 使用 width + padding-bottom%(=高/宽×100%) 占位，缩略图在绝对定位层内 contain。
 */
function DisplayCardFrame({
  page,
  displayTitle,
  className,
  children,
  style,
  selectionMode = false,
  selectChecked = false,
  onSelectCheckedChange,
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  page: PageType | null;
  displayTitle?: string | null;
  selectionMode?: boolean;
  selectChecked?: boolean;
  onSelectCheckedChange?: (checked: boolean) => void;
}) {
  const ratioPct = useMemo(() => {
    const w = page?.size?.width ?? 0;
    const h = page?.size?.height ?? 0;
    if (w > 0 && h > 0) return (h / w) * 100;
    return FALLBACK_PREVIEW_RATIO_PCT;
  }, [page]);

  const titleLabel =
    typeof displayTitle === 'string' && displayTitle.trim() ? displayTitle.trim() : '未命名';

  return (
    <div
      {...rest}
      style={style}
      className={cn(
        'group relative w-full overflow-hidden rounded-lg border border-border bg-muted transition-shadow hover:shadow-md',
        className,
      )}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{ paddingBottom: `${ratioPct}%` }}
      >
        <div className="absolute inset-0">
          {selectionMode ? (
            <div
              className="absolute top-2 right-2 z-20"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Checkbox
                isSelected={selectChecked}
                onChange={(next) => onSelectCheckedChange?.(next)}
                aria-label="选择大屏"
                className="bg-background/90 backdrop-blur-sm"
              >
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
              </Checkbox>
            </div>
          ) : null}
          {children}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 translate-y-full bg-invert/80 px-2.5 py-2 text-left text-xs font-medium text-invert-foreground transition-transform duration-200 ease-out group-hover:translate-y-0 group-focus-within:translate-y-0"
            aria-hidden
          >
            <span className="line-clamp-2">{titleLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

type KnowledgeBaseItem = {
  id: string;
  ownerId?: string | null;
  title?: string | null;
  content?: unknown;
  meta?: unknown;
  coverImage?: string | null;
  fileFormat?: 'slides' | 'doc' | 'display' | null;
  isTemplate?: boolean | null;
  isSharedToSpace?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

function getFirstPageFromContent(item: KnowledgeBaseItem): PageType | null {
  const raw = item.content;
  if (raw == null || typeof raw !== 'object') return null;
  const parsed = parseCanvasData(raw);
  if (!parsed?.pages?.length) return null;
  return parsed.pages[0] as PageType;
}

function hasPresentableContent(item: KnowledgeBaseItem): boolean {
  const raw = item.content;
  if (raw == null || typeof raw !== 'object') return false;
  const parsed = parseCanvasData(raw);
  return Boolean(parsed?.pages?.length);
}

/** 校验并规范化模板 content，供 create 写入新大屏 */
function cloneTemplateContentForCreate(item: KnowledgeBaseItem): Record<string, unknown> | null {
  const raw = item.content;
  if (raw == null || typeof raw !== 'object') return null;
  const parsed = parseCanvasData(raw);
  if (!parsed?.pages?.length) return null;
  const doc = canvasDataFromParsed(parsed);
  return JSON.parse(JSON.stringify(doc)) as Record<string, unknown>;
}

function KnowledgeBaseCardToolbar({
  item,
  currentUserId,
  onPresent,
  onEdit,
  onUseTemplateNew,
  templateCreatePending,
  onDelete,
  onSetTemplate,
  onUnsetTemplate,
  onShareToSpace,
  onUnshareSpace,
  updatePending,
}: {
  item: KnowledgeBaseItem;
  currentUserId: string | null | undefined;
  onPresent: () => void;
  onEdit: () => void;
  onUseTemplateNew: () => void;
  templateCreatePending?: boolean;
  onDelete: () => void;
  onSetTemplate: () => void;
  onUnsetTemplate: () => void;
  onShareToSpace: () => void;
  onUnshareSpace: () => void;
  updatePending?: boolean;
}) {
  const updated = item.updatedAt ?? item.createdAt ?? '';
  const timeStr =
    typeof updated === 'string' && updated ? formatRelativeTime(updated) : '—';

  const canManage = Boolean(
    currentUserId &&
      typeof item.ownerId === 'string' &&
      item.ownerId.length > 0 &&
      item.ownerId === currentUserId,
  );
  const ownerActionDisabled = Boolean(updatePending);
  const presentDisabled = !hasPresentableContent(item);
  const isTemplate = Boolean(item.isTemplate);
  const isSharedToSpace = Boolean(item.isSharedToSpace);

  return (
    <div className="mt-2 flex min-w-0 items-center justify-between gap-1">
      <span
        className="min-w-0 flex-1 truncate text-xs text-muted-foreground"
        title={typeof updated === 'string' && updated ? formatDateTime(updated) : undefined}
      >
        {timeStr}
      </span>
      <Dropdown>
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="更多操作"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <IconDotsFilled className="size-4" stroke={1.5} />
        </Button>
        <Dropdown.Popover className="min-w-44" placement="bottom end">
          <Dropdown.Menu>
            <Dropdown.Item
              id="present"
              textValue="演示"
              isDisabled={presentDisabled}
              onAction={() => onPresent()}
            >
              <Label className="flex items-center gap-2">
                <IconPlayerPlay className="size-4" stroke={1.5} />
                演示
              </Label>
            </Dropdown.Item>
            {canManage ? (
              <Dropdown.Item
                id="edit"
                textValue="编辑"
                isDisabled={ownerActionDisabled}
                onAction={() => onEdit()}
              >
                <Label className="flex items-center gap-2">
                  <IconEdit className="size-4" stroke={1.5} />
                  编辑
                </Label>
              </Dropdown.Item>
            ) : null}
            {isTemplate ? (
              <Dropdown.Item
                id="use-template-new"
                textValue="使用模板新建"
                isDisabled={templateCreatePending}
                onAction={() => onUseTemplateNew()}
              >
                <Label className="flex items-center gap-2">
                  <IconPhotoPlus className="size-4" stroke={1.5} />
                  使用模板新建
                </Label>
              </Dropdown.Item>
            ) : null}
            {canManage ? (
              <Dropdown.Item
                id="template"
                textValue={isTemplate ? '撤销模板' : '设为模板'}
                isDisabled={ownerActionDisabled}
                onAction={() => (isTemplate ? onUnsetTemplate() : onSetTemplate())}
              >
                <Label className="flex items-center gap-2">
                  {isTemplate ? (
                    <IconPhotoX className="size-4" stroke={1.5} />
                  ) : (
                    <IconPhotoCheck className="size-4" stroke={1.5} />
                  )}
                  {isTemplate ? '撤销模板' : '设为模板'}
                </Label>
              </Dropdown.Item>
            ) : null}
            {canManage ? (
              <Dropdown.Item
                id="space-share"
                textValue={isSharedToSpace ? '撤销空间分享' : '分享到空间'}
                isDisabled={ownerActionDisabled}
                onAction={() => (isSharedToSpace ? onUnshareSpace() : onShareToSpace())}
              >
                <Label className="flex items-center gap-2">
                  {isSharedToSpace ? (
                    <IconShareOff className="size-4" stroke={1.5} />
                  ) : (
                    <IconShare className="size-4" stroke={1.5} />
                  )}
                  {isSharedToSpace ? '撤销空间分享' : '分享到空间'}
                </Label>
              </Dropdown.Item>
            ) : null}
            {canManage ? (
              <Dropdown.Item
                id="delete"
                textValue="删除"
                variant="danger"
                isDisabled={ownerActionDisabled}
                onAction={() => onDelete()}
              >
                <Label className="flex items-center gap-2">
                  <IconTrash className="size-4" stroke={1.5} />
                  删除
                </Label>
              </Dropdown.Item>
            ) : null}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </div>
  );
}

const masonryBreakpointCols = {
  default: 8,
  1280: 6,
  768: 4,
  480: 2,
};

export default function DisplaysPage() {
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBaseItem | null>(null);
  const [deleteTitleLabel, setDeleteTitleLabel] = useState('');
  const deleteTargetRef = useRef<KnowledgeBaseItem | null>(null);

  const [displaySourceScope, setDisplaySourceScope] = useState<'mine' | 'space'>('mine');
  const [displayListScope, setDisplayListScope] = useState<'all' | 'templates'>('all');
  const [displaySelectionMode, setDisplaySelectionMode] = useState(false);
  const [selectedDisplayIds, setSelectedDisplayIds] = useState(() => new Set<string>());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<KnowledgeBaseItem[]>([]);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  /** 与 IntersectionObserver root 一致：内层滚动容器（避免与 AppShell 主区 overflow-auto 抢滚轮时 root=null 检测失效） */
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const loadMoreGateRef = useRef({ hasNext: false, isFetching: false });

  const currentUserId = useAppSelector((s) => s.auth.user?.id ?? null);

  const utils = trpc.useUtils();
  const createDisplayMutation = trpc.knowledgeBases.create.useMutation();
  const updateDisplayMutation = trpc.knowledgeBases.update.useMutation({
    onSuccess: (_data, variables) => {
      const { id, data: patch } = variables;
      const nextIsTemplate = patch.isTemplate;
      const nextIsSharedToSpace = patch.isSharedToSpace;
      setAccumulated((prev) => {
        const merged = prev.map((it) => {
          if (it.id !== id) return it;
          return {
            ...it,
            ...(nextIsTemplate !== undefined && { isTemplate: nextIsTemplate }),
            ...(nextIsSharedToSpace !== undefined && { isSharedToSpace: nextIsSharedToSpace }),
          };
        });
        return merged.filter((it) => {
          if (displaySourceScope === 'space' && !it.isSharedToSpace) return false;
          if (displayListScope === 'templates' && !it.isTemplate) return false;
          return true;
        });
      });
      toast.success('已更新');
    },
    onError: () => {
      toast.danger('操作失败');
    },
  });
  const deleteMutation = trpc.knowledgeBases.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.knowledgeBases.listMy.invalidate(),
        utils.knowledgeBases.list.invalidate(),
      ]);
      setPage(1);
      setAccumulated([]);
      toast.success('已删除');
    },
    onError: () => {
      toast.danger('删除失败');
    },
  });

  const listQueryBase = useMemo(
    () =>
      ({
        sortBy: 'updatedAt' as const,
        sortOrder: 'desc' as const,
        where: undefined,
        fileFormat: 'display' as const,
        pageSize: DISPLAY_PAGE_SIZE,
      }) as const,
    [],
  );

  const listMyAllQuery = trpc.knowledgeBases.listMy.useQuery(
    { ...listQueryBase, page },
    { enabled: displaySourceScope === 'mine' && displayListScope === 'all' },
  );
  const listMyTemplatesQuery = trpc.knowledgeBases.listMy.useQuery(
    { ...listQueryBase, page, isTemplate: true },
    { enabled: displaySourceScope === 'mine' && displayListScope === 'templates' },
  );
  const listSpaceAllQuery = trpc.knowledgeBases.list.useQuery(
    { ...listQueryBase, page, isSharedToSpace: true },
    { enabled: displaySourceScope === 'space' && displayListScope === 'all' },
  );
  const listSpaceTemplatesQuery = trpc.knowledgeBases.list.useQuery(
    { ...listQueryBase, page, isSharedToSpace: true, isTemplate: true },
    { enabled: displaySourceScope === 'space' && displayListScope === 'templates' },
  );

  const data =
    displaySourceScope === 'mine'
      ? displayListScope === 'all'
        ? listMyAllQuery.data
        : listMyTemplatesQuery.data
      : displayListScope === 'all'
        ? listSpaceAllQuery.data
        : listSpaceTemplatesQuery.data;
  const activeListQuery =
    displaySourceScope === 'mine'
      ? displayListScope === 'all'
        ? listMyAllQuery
        : listMyTemplatesQuery
      : displayListScope === 'all'
        ? listSpaceAllQuery
        : listSpaceTemplatesQuery;
  const showGridLoading = activeListQuery.isPending && !activeListQuery.data && page === 1;
  const isFetching =
    displaySourceScope === 'mine'
      ? displayListScope === 'all'
        ? listMyAllQuery.isFetching
        : listMyTemplatesQuery.isFetching
      : displayListScope === 'all'
        ? listSpaceAllQuery.isFetching
        : listSpaceTemplatesQuery.isFetching;
  const isError =
    displaySourceScope === 'mine'
      ? displayListScope === 'all'
        ? listMyAllQuery.isError
        : listMyTemplatesQuery.isError
      : displayListScope === 'all'
        ? listSpaceAllQuery.isError
        : listSpaceTemplatesQuery.isError;

  const displayFiltersInitial = useRef(true);
  useEffect(() => {
    if (displayFiltersInitial.current) {
      displayFiltersInitial.current = false;
      return;
    }
    setPage(1);
    setAccumulated([]);
    setDisplaySelectionMode(false);
    setSelectedDisplayIds(new Set());
  }, [displayListScope, displaySourceScope]);

  const deletableSelectedItems = useMemo(() => {
    if (!currentUserId) return [];
    return accumulated.filter(
      (i) => selectedDisplayIds.has(i.id) && i.ownerId === currentUserId,
    );
  }, [accumulated, currentUserId, selectedDisplayIds]);

  const setDisplaySelected = useCallback((id: string, checked: boolean) => {
    setSelectedDisplayIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!data?.items) return;
    const batch = data.items as KnowledgeBaseItem[];
    setAccumulated((prev) => {
      if (page === 1) return batch;
      const seen = new Set(prev.map((i) => i.id));
      const next = [...prev];
      for (const it of batch) {
        if (!seen.has(it.id)) {
          seen.add(it.id);
          next.push(it);
        }
      }
      return next;
    });
  }, [data, page]);

  const hasNext = data?.hasNext ?? false;
  loadMoreGateRef.current = { hasNext, isFetching };

  useEffect(() => {
    const root = scrollRootRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        const { hasNext: nextOk, isFetching: busy } = loadMoreGateRef.current;
        if (!hit || !nextOk || busy) return;
        setPage((p) => p + 1);
      },
      { root, rootMargin: '320px', threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [accumulated.length, hasNext]);

  const openNewDisplayCanvas = useCallback(async () => {
    try {
      const data = await createDisplayMutation.mutateAsync({
        title: '未命名',
        fileFormat: 'display',
        content: buildInitialCanvasContent(DEFAULT_SLIDE_SIZE),
      });
      navigate(`/displays/canvas/${data.id}`);
    } catch {
      toast.danger('创建大屏失败');
    }
  }, [createDisplayMutation, navigate]);

  const handleOpenCanvas = useCallback(
    (media: KnowledgeBaseItem) => {
      navigate(`/displays/canvas/${media.id}`);
    },
    [navigate],
  );

  const handlePresentCanvas = useCallback(
    (media: KnowledgeBaseItem) => {
      navigate(`/displays/canvas/${media.id}/play`);
    },
    [navigate],
  );

  const handleOpenNewFromTemplate = useCallback(
    async (media: KnowledgeBaseItem) => {
      if (createDisplayMutation.isPending) return;
      const content = cloneTemplateContentForCreate(media);
      if (!content) {
        toast.danger('模板无有效画布数据');
        return;
      }
      const baseTitle = media.title?.trim() || '未命名';
      try {
        const data = await createDisplayMutation.mutateAsync({
          title: `${baseTitle} 副本`,
          fileFormat: 'display',
          content,
        });
        navigate(`/displays/canvas/${data.id}`);
      } catch {
        toast.danger('从模板创建大屏失败');
      }
    },
    [createDisplayMutation, navigate],
  );

  const requestDeleteKnowledgeBase = useCallback((item: KnowledgeBaseItem) => {
    deleteTargetRef.current = item;
    setDeleteTitleLabel(item.title?.trim() ? item.title.trim() : '未命名');
    setDeleteTarget(item);
  }, []);

  const handleDeleteDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      deleteTargetRef.current = null;
      setDeleteTitleLabel('');
      setDeleteTarget(null);
    }
  }, []);

  const confirmDeleteKnowledgeBase = useCallback(async () => {
    const item = deleteTargetRef.current;
    if (!item) return;
    try {
      await deleteMutation.mutateAsync({ id: item.id });
      deleteTargetRef.current = null;
      setDeleteTitleLabel('');
      setDeleteTarget(null);
    } catch {
      /* toast in mutation */
    }
  }, [deleteMutation]);

  const confirmBulkDeleteDisplays = useCallback(async () => {
    const items = deletableSelectedItems;
    if (items.length === 0) return;
    try {
      for (const it of items) {
        await trpcProxyClient.knowledgeBases.delete.mutate({ id: it.id });
      }
      await Promise.all([
        utils.knowledgeBases.listMy.invalidate(),
        utils.knowledgeBases.list.invalidate(),
      ]);
      setPage(1);
      setAccumulated([]);
      setBulkDeleteOpen(false);
      setDisplaySelectionMode(false);
      setSelectedDisplayIds(new Set());
      toast.success(`已删除 ${items.length} 项`);
    } catch {
      toast.danger('删除失败');
    }
  }, [
    deletableSelectedItems,
    utils.knowledgeBases.list,
    utils.knowledgeBases.listMy,
  ]);

  return (
    <>
      <style>{`
        .displays-masonry-grid { display: flex; margin-left: -1rem; width: auto; }
        .displays-masonry-grid_column { padding-left: 1rem; background-clip: padding-box; }
      `}</style>

      <AlertDialog isOpen={deleteTarget !== null} onOpenChange={handleDeleteDialogOpenChange}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading className="text-foreground font-bold">删除确认</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除
                <span className="mx-0.5 font-bold text-primary underline">{deleteTitleLabel}</span>
                吗？ 删除后将不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => handleDeleteDialogOpenChange(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending}
                  onPress={() => void confirmDeleteKnowledgeBase()}
                >
                  {deleteMutation.isPending ? '删除中…' : '删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AlertDialog isOpen={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading className="text-foreground font-bold">批量删除确认</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定删除已选中的 {deletableSelectedItems.length} 项大屏吗？删除后将不可恢复。
                {selectedDisplayIds.size > deletableSelectedItems.length ? (
                  <span className="mt-2 block text-muted-foreground">
                    （仅包含您拥有删除权限的项，其余已自动排除。）
                  </span>
                ) : null}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setBulkDeleteOpen(false)}>
                  取消
                </Button>
                <Button variant="danger" onPress={() => void confirmBulkDeleteDisplays()}>
                  删除
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <div className="flex h-full min-h-0 flex-col">
        <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-border p-4">
          <h1 className="justify-self-start text-xl font-semibold">大屏</h1>
          <Segment
            className="justify-self-center"
            selectedKey={displaySourceScope}
            onSelectionChange={(key) => {
              const k = String(key);
              if (k === 'mine' || k === 'space') setDisplaySourceScope(k);
            }}
          >
            <Segment.Item id="mine">
              <Segment.Separator />
              我的
            </Segment.Item>
            <Segment.Item id="space">
              <Segment.Separator />
              共享
            </Segment.Item>
          </Segment>
          <div className="justify-self-end">
            <Button variant="primary" onPress={() => void openNewDisplayCanvas()}>
              新建大屏
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border px-4">
            <div className="flex min-w-0 items-center">
              <Segment
                size="sm"
                selectedKey={displayListScope}
                onSelectionChange={(key) => {
                  const k = String(key);
                  if (k === 'all' || k === 'templates') setDisplayListScope(k);
                }}
              >
                <Segment.Item id="all">
                  <Segment.Separator />
                  全部
                </Segment.Item>
                <Segment.Item id="templates">
                  <Segment.Separator />
                  模板
                </Segment.Item>
              </Segment>
            </div>
            <div className="relative flex min-h-8 shrink-0 items-center justify-end">
              <AnimatePresence mode="wait" initial={false}>
                {displaySelectionMode ? (
                  <motion.div
                    key="display-selection-actions"
                    initial={{ opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 28 }}
                    transition={{ duration: 0.12, ease: 'easeOut' }}
                    className="flex items-center gap-2"
                  >
                    <Button
                      variant="danger"
                      size="sm"
                      className="shrink-0 gap-1"
                      isDisabled={deletableSelectedItems.length === 0}
                      onPress={() => setBulkDeleteOpen(true)}
                    >
                      <IconTrash className="size-3.5" stroke={1.5} />
                      删除
                    </Button>
                    <ButtonGroup size="sm" variant="secondary">
                      <Button
                        onPress={() => {
                          setSelectedDisplayIds(new Set(accumulated.map((i) => i.id)));
                        }}
                      >
                        全选
                      </Button>
                      <Button
                        onPress={() => {
                          setSelectedDisplayIds((prev) => {
                            const all = new Set(accumulated.map((i) => i.id));
                            const inverted = new Set<string>();
                            for (const id of all) {
                              if (!prev.has(id)) inverted.add(id);
                            }
                            return inverted;
                          });
                        }}
                      >
                        反选
                      </Button>
                      <Button
                        onPress={() => {
                          setDisplaySelectionMode(false);
                          setSelectedDisplayIds(new Set());
                        }}
                      >
                        取消
                      </Button>
                    </ButtonGroup>
                  </motion.div>
                ) : (
                  <motion.div
                    key="display-select-entry"
                    initial={{ opacity: 0, x: 0 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 0 }}
                    transition={{ duration: 0.1, ease: 'easeOut' }}
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      className="gap-1.5"
                      onPress={() => setDisplaySelectionMode(true)}
                    >
                      <IconPhotoCheck className="size-4 shrink-0" stroke={1.5} />
                      选择
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div
            ref={scrollRootRef}
            className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-3"
          >
            {showGridLoading ? (
              <div className="text-sm text-muted-foreground">加载中…</div>
            ) : isError ? (
              <div className="text-sm text-destructive">加载失败</div>
            ) : accumulated.length === 0 ? (
              <div className="flex w-full justify-center py-8">
                <div
                  className={cn(
                    'flex w-full max-w-[288px] flex-col overflow-hidden rounded-lg border-2 border-dashed border-border bg-muted/20',
                    'aspect-[9/16]',
                  )}
                >
                  <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      {displaySourceScope === 'space'
                        ? displayListScope === 'templates'
                          ? '暂无共享模板'
                          : '暂无空间共享的大屏'
                        : displayListScope === 'templates'
                          ? '暂无模板'
                          : '还没有大屏呢'}
                    </p>
                    {displaySourceScope === 'mine' && displayListScope === 'all' ? (
                      <Button onPress={() => void openNewDisplayCanvas()}>立即创建</Button>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Masonry
                  breakpointCols={masonryBreakpointCols}
                  className="displays-masonry-grid"
                  columnClassName="displays-masonry-grid_column"
                >
                  {accumulated.map((item) => {
                    const firstPage = getFirstPageFromContent(item);
                    return (
                      <div key={item.id} className="mb-4 break-inside-avoid">
                        <DisplayCardFrame
                          page={firstPage}
                          displayTitle={item.title}
                          role="button"
                          tabIndex={displaySelectionMode ? -1 : 0}
                          aria-pressed={displaySelectionMode ? selectedDisplayIds.has(item.id) : undefined}
                          aria-label={
                            displaySelectionMode
                              ? selectedDisplayIds.has(item.id)
                                ? '取消选择大屏'
                                : '选择大屏'
                              : displayListScope === 'templates'
                                ? '用模板新建大屏'
                                : '打开编辑'
                          }
                          className="cursor-pointer"
                          selectionMode={displaySelectionMode}
                          selectChecked={selectedDisplayIds.has(item.id)}
                          onSelectCheckedChange={(checked) =>
                            setDisplaySelected(item.id, checked)
                          }
                          onClick={() => {
                            if (displaySelectionMode) {
                              setDisplaySelected(item.id, !selectedDisplayIds.has(item.id));
                            } else if (displayListScope === 'templates') {
                              void handleOpenNewFromTemplate(item);
                            } else {
                              handleOpenCanvas(item);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (displaySelectionMode) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (displayListScope === 'templates') {
                                void handleOpenNewFromTemplate(item);
                              } else {
                                handleOpenCanvas(item);
                              }
                            }
                          }}
                        >
                          {firstPage ? (
                            <ThumbnailFill page={firstPage} fit="contain" />
                          ) : (
                            <div className="flex size-full items-center justify-center text-muted-foreground text-xs">
                              无预览
                            </div>
                          )}
                        </DisplayCardFrame>
                        <KnowledgeBaseCardToolbar
                          item={item}
                          currentUserId={currentUserId}
                          onPresent={() => handlePresentCanvas(item)}
                          onEdit={() => handleOpenCanvas(item)}
                          onUseTemplateNew={() => void handleOpenNewFromTemplate(item)}
                          templateCreatePending={createDisplayMutation.isPending}
                          onDelete={() => requestDeleteKnowledgeBase(item)}
                          onSetTemplate={() =>
                            updateDisplayMutation.mutate({
                              id: item.id,
                              data: { isTemplate: true },
                            })
                          }
                          onUnsetTemplate={() =>
                            updateDisplayMutation.mutate({
                              id: item.id,
                              data: { isTemplate: false },
                            })
                          }
                          onShareToSpace={() =>
                            updateDisplayMutation.mutate({
                              id: item.id,
                              data: { isSharedToSpace: true },
                            })
                          }
                          onUnshareSpace={() =>
                            updateDisplayMutation.mutate({
                              id: item.id,
                              data: { isSharedToSpace: false },
                            })
                          }
                          updatePending={updateDisplayMutation.isPending}
                        />
                      </div>
                    );
                  })}
                </Masonry>
                <div ref={loadMoreSentinelRef} className="h-8 shrink-0" aria-hidden />
                {isFetching && page > 1 ? (
                  <p className="py-2 text-center text-xs text-muted-foreground">加载更多…</p>
                ) : null}
                {!hasNext && accumulated.length > 0 ? (
                  <p className="py-2 text-center text-xs text-muted-foreground">已加载全部</p>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

    </>
  );
}
